import { RateMasterService } from "@/api/services/masterService";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

// Define types for Rate
 interface RateData {
    id: string;
    currencyId: string;
    isd: string;
    currencyName: string;
    typeId: string;
    typeName: string;
    marginTypeId: string;
    marginName: string;
    unit: string;
    currencyBuyMargin: number;
    currencySellMargin: number;
    vtmSettMargin: number;
    vtmSurrMargin: number;
    minimunBuyRate: number;
    maximunBuyRate: number;
    minimumSellRate: number;
    maximumSellRate: number;
    branchId: string;
    rate: number;
    marginDate: string; // ISO date string
    status: number;
    currencySell: number | null;
    currencyBuy: number | null;
  }

// Define state type
interface RateMasterState {
  loading: boolean;
  RateList: RateData[];
  purchasebydate: RateData[];
  error?: string;
}

// Initial state with type safety
const initialState: RateMasterState = {
  loading: false,
  RateList: [],
  purchasebydate: [],
  error: undefined,
};

// ✅ Fixed Async thunk with proper return type
export const fetchRateMaster = createAsyncThunk<
RateData[], 
  void, 
  { rejectValue: string }
>(
  "Rate/fetchRateMaster",
  async (_, { rejectWithValue }) => {

    try {
      const response = await RateMasterService.getall();

      // If API returns { data: Rate[] }, extract `data`
      return response.data ? response.data : response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch Rate data");
    }
  }
);
export const fetchsalepurchasebyDate = createAsyncThunk<
  RateData[],  // ✅ Expected return type (array of RateData)
  Date,        // ✅ The parameter type (date)
  { rejectValue: string }
>(
  "Rate/fetchsalepurchasebyDate",
  async (date, { rejectWithValue }) => {
    try {
      const response = await RateMasterService.getsalepurchasebydate(date);

      // Ensure we return the correct data format
      return response?.data ?? response; 
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch Rate data");
    }
  }
);


// Create slice with strong typing
const RateMasterSlice = createSlice({
  name: "RateMaster",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRateMaster.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchRateMaster.fulfilled, (state, action: PayloadAction<RateData[]>) => {
        state.RateList = action.payload;
        state.loading = false;
      })
      .addCase(fetchRateMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      .addCase(fetchsalepurchasebyDate.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchsalepurchasebyDate.fulfilled, (state, action: PayloadAction<RateData[]>) => {
        state.purchasebydate = action.payload;
        state.loading = false;
      })
      .addCase(fetchsalepurchasebyDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

// Export reducer
export default RateMasterSlice.reducer;
